import React from 'react';
import { FlatList, Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDeals } from '../../src/data';
import { GROUPS, type Group, groupOf, price, timeAgo } from '../../src/format';
import type { Deal } from '../../src/types';

/**
 * S_TRACK — 인더스트리얼 방향 시안.
 *
 * 지금까지의 시안이 실패한 이유는 색과 여백만 만졌기 때문이다. 이번엔 구조를 바꾼다.
 * 딜 앱은 사진으로 훑는 물건이라 2열 그리드로 가고, 사진이 카드의 절반을 차지한다.
 *
 * 사진이 없는 딜이 아직 많다. 그리드에서 빈 칸은 목록에서보다 훨씬 크게 망가져 보이므로,
 * 사진 자리를 비우지 않고 상품명을 큰 타이포로 채워 '의도한 칸'으로 만든다.
 */
const P = {
  ground: '#C8C8C6', // 콘크리트
  panel: '#FFFFFF',
  ink: '#111111',
  ink2: '#6E6E6B',
  ink3: '#9A9A96',
  line: '#D9D9D6',
  volt: '#E9FF00', // 형광 노랑 — 지금 선택된 것과 행동에만
  alert: '#E4002B',
} as const;

export default function DesignS() {
  const insets = useSafeAreaInsets();
  const deals = useDeals();
  const [group, setGroup] = React.useState<Group>('전체');
  // 열 수는 눌러서 바꿔 본다. 3열은 한 화면에 더 담기지만 한국어 상품명이 그만큼 잘린다
  const [cols, setCols] = React.useState<2 | 3>(2);
  const shown = React.useMemo(
    () => (group === '전체' ? deals : deals.filter((d) => groupOf(d) === group)),
    [deals, group],
  );

  return (
    <View style={{ flex: 1, backgroundColor: P.ground }}>
      <View style={[s.head, { paddingTop: insets.top + 18 }]}>
        <Text style={s.brand}>S_TRACK</Text>
        <Text style={s.brandSub}>PRICE INTELLIGENCE V.2.04</Text>
      </View>

      <View style={s.bar}>
        <View style={{ gap: 1 }}>
          <Text style={s.kicker}>MONITORING ACTIVE</Text>
          <Text style={s.h1}>오늘의 딜</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Pressable onPress={() => setCols(cols === 2 ? 3 : 2)} style={s.colBtn}>
            <Text style={s.colBtnText}>{cols}열</Text>
          </Pressable>
          <Text style={s.live}>{`{LIVE_${String(shown.length).padStart(3, '0')}}`}</Text>
        </View>
      </View>

      <FlatList
        data={shown}
        keyExtractor={(d) => d.id}
        key={cols}
        numColumns={cols}
        columnWrapperStyle={{ gap: 8, paddingHorizontal: 16 }}
        contentContainerStyle={{ gap: 8, paddingBottom: 24 }}
        ListHeaderComponent={<Nav group={group} onPick={setGroup} />}
        ListFooterComponent={
          <View style={s.engine}>
            <Text style={s.engineLabel}>NEURAL_SEARCH_ENGINE</Text>
            <Text style={s.engineBody}>
              찾는 걸 말하듯 적으면 AI 가 되물어 가며 같이 좁혀 줍니다
            </Text>
          </View>
        }
        renderItem={({ item }) => <Tile deal={item} cols={cols} />}
      />

    </View>
  );
}

function Nav({ group, onPick }: { group: Group; onPick: (g: Group) => void }) {
  return (
    <View style={s.nav}>
      {GROUPS.map((g) => (
        <Pressable key={g} onPress={() => onPick(g)} style={[s.chip, g === group && s.chipOn]}>
          <Text style={[s.chipText, g === group && s.chipTextOn]}>{g}</Text>
        </Pressable>
      ))}
    </View>
  );
}

function discount(d: Deal): number | null {
  if (!d.market_price || !d.effective_price) return null;
  const pct = Math.round((1 - d.effective_price / d.market_price) * 100);
  return pct >= 5 ? pct : null;
}

function Tile({ deal, cols }: { deal: Deal; cols: 2 | 3 }) {
  const tight = cols === 3;
  const [broken, setBroken] = React.useState(false);
  const thumb = !broken ? deal.thumb_url : null;
  const off = discount(deal);
  return (
    <Pressable style={({ pressed }) => [s.tile, pressed && { opacity: 0.9 }]}>
      <View style={s.shot}>
        {thumb ? (
          <Image source={{ uri: thumb }} style={{ width: '100%', height: '100%' }} resizeMode="cover" onError={() => setBroken(true)} />
        ) : (
          // 사진을 아직 못 구한 딜. 상품명을 또 쓰면 아래와 겹치니 점수를 채운다
          <View style={s.noShot}>
            <Text style={[s.scoreNum, tight && { fontSize: 30 }]}>{Math.round(deal.score)}</Text>
            <Text style={s.scoreUnit}>점</Text>
          </View>
        )}
        {off !== null ? (
          <View style={s.offTag}>
            <Text style={s.offText}>-{off}%</Text>
          </View>
        ) : null}
      </View>

      <View style={s.meta}>
        <Text style={s.mall} numberOfLines={1}>
          {(deal.mall ?? '판매처 미확인').toUpperCase()}
        </Text>
        <Text style={[s.name, tight && { fontSize: 11.5, lineHeight: 15 }]} numberOfLines={2}>
          {deal.product}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 3 }}>
          <Text style={[s.price, tight && { fontSize: 15, letterSpacing: -0.4 }]}>{price(deal).replace('원', '')}</Text>
          <Text style={s.won}>₩</Text>
        </View>
        <Text style={s.time}>{deal.posted_at ? timeAgo(deal.posted_at).toUpperCase() : ''}</Text>
      </View>
    </Pressable>
  );
}

function Tab({ label, sub, active }: { label: string; sub: string; active?: boolean }) {
  return (
    <View style={[s.tab, active && { backgroundColor: P.volt }]}>
      <Text style={[s.tabLabel, active && { color: P.ink }]}>{label}</Text>
      <Text style={[s.tabSub, active && { color: P.ink }]}>{sub}</Text>
    </View>
  );
}

const s = StyleSheet.create({
  head: { paddingHorizontal: 16, paddingBottom: 14 },
  brand: { fontSize: 30, fontWeight: '900', color: P.ink, letterSpacing: 1.5 },
  brandSub: { fontSize: 10, fontWeight: '700', color: P.ink2, letterSpacing: 1.6, marginTop: 2 },

  bar: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', paddingHorizontal: 16, paddingBottom: 12 },
  kicker: { fontSize: 9.5, fontWeight: '800', color: P.ink3, letterSpacing: 1.4 },
  h1: { fontSize: 22, fontWeight: '900', color: P.ink, letterSpacing: -0.4 },
  colBtn: { borderWidth: 1, borderColor: P.ink, paddingVertical: 3, paddingHorizontal: 8, backgroundColor: P.volt },
  colBtnText: { fontSize: 11, fontWeight: '900', color: P.ink },
  live: { fontSize: 10, fontWeight: '700', color: P.ink2, letterSpacing: 0.8, fontVariant: ['tabular-nums'] },

  nav: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingHorizontal: 16, paddingBottom: 12 },
  chip: { paddingVertical: 6, paddingHorizontal: 11, backgroundColor: P.panel, borderWidth: 1, borderColor: P.ink },
  chipOn: { backgroundColor: P.ink },
  chipText: { fontSize: 11.5, fontWeight: '800', color: P.ink },
  chipTextOn: { color: P.volt },

  tile: { flex: 1, backgroundColor: P.panel, borderWidth: 1, borderColor: P.ink },
  shot: { aspectRatio: 1, backgroundColor: P.line, borderBottomWidth: 1, borderBottomColor: P.ink },
  noShot: { flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 2, backgroundColor: P.ink },
  scoreNum: { fontSize: 44, fontWeight: '900', color: P.volt, letterSpacing: -2, fontVariant: ['tabular-nums'] },
  scoreUnit: { fontSize: 13, fontWeight: '800', color: P.volt, marginTop: 16 },
  offTag: { position: 'absolute', top: 0, right: 0, backgroundColor: P.alert, paddingVertical: 3, paddingHorizontal: 7 },
  offText: { fontSize: 11, fontWeight: '900', color: '#FFFFFF', letterSpacing: 0.3 },

  meta: { padding: 10, gap: 3 },
  mall: { fontSize: 9, fontWeight: '800', color: P.ink3, letterSpacing: 0.9 },
  name: { fontSize: 13, fontWeight: '700', color: P.ink, lineHeight: 17 },
  price: { fontSize: 19, fontWeight: '900', color: P.ink, letterSpacing: -0.6, fontVariant: ['tabular-nums'] },
  won: { fontSize: 11, fontWeight: '700', color: P.ink2 },
  time: { fontSize: 9, fontWeight: '700', color: P.ink3, letterSpacing: 0.6, marginTop: 1 },

  engine: { marginHorizontal: 16, marginTop: 4, backgroundColor: P.ink, padding: 14, gap: 5 },
  engineLabel: { fontSize: 9.5, fontWeight: '800', color: P.volt, letterSpacing: 1.4 },
  engineBody: { fontSize: 14, fontWeight: '700', color: '#FFFFFF', lineHeight: 20 },

  tabs: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: P.ink, backgroundColor: P.panel },
  tab: { flex: 1, alignItems: 'center', paddingVertical: 10, gap: 1, borderRightWidth: 1, borderRightColor: P.ink },
  tabLabel: { fontSize: 10.5, fontWeight: '900', color: P.ink, letterSpacing: 0.8 },
  tabSub: { fontSize: 9, fontWeight: '700', color: P.ink3 },
});
